import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFeedbackList } from '../../api/client';

/**
 * Feedback inbox — admin/viewer view of every thumbs-up/down submission with
 * the contact details and comment a user provided through the chat popup.
 */
export default function FeedbackTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');    // all | down | up | hasComment
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFeedbackList(500);
      setItems(res.data.items || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Only show entries that came from the feedback popup.  Bare 👍 / 👎 clicks
  // have no comment and no contact fields — those aren't actionable signals
  // for this view.  Popup submissions always carry a comment (required field).
  const popupItems = useMemo(
    () => items.filter((r) => (r.comment && r.comment.trim().length > 0)
                            || r.first_name || r.last_name || r.email),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return popupItems.filter((r) => {
      if (filter === 'hasComment' && !r.comment) return false;
      if (!q) return true;
      const hay = [
        r.first_name, r.last_name, r.email,
        r.comment, r.question, r.answer_preview,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [popupItems, filter, search]);

  const counts = useMemo(() => {
    let withComment = 0;
    popupItems.forEach((r) => { if (r.comment) withComment++; });
    return { withComment, total: popupItems.length };
  }, [popupItems]);

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:'#0f172a' }}>Feedback Inbox</h2>
        </div>
        <button
          onClick={fetchAll}
          style={{
            padding:'8px 14px', border:'1px solid #cbd5e1',
            background:'#fff', borderRadius:'var(--radius-btn, 10px)',
            fontSize:13, fontWeight:600, color:'#475569',
            cursor:'pointer',
          }}
        >↻ Refresh</button>
      </div>

      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:10, marginBottom:16 }}>
        <Kpi label="Total feedback" value={counts.total} />
        <Kpi label="With comment" value={counts.withComment} tint="#4f46e5" />
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
        {[
          { k: 'all',        label: 'All' },
          { k: 'hasComment', label: 'With comment' },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            style={{
              padding:'6px 13px', borderRadius:999,
              border: filter === f.k ? '1px solid #1d9c9c' : '1px solid #e2e8f0',
              background: filter === f.k ? '#1d9c9c' : '#fff',
              color: filter === f.k ? '#fff' : '#475569',
              fontSize:12, fontWeight:600, cursor:'pointer',
            }}
          >{f.label}</button>
        ))}
        <div style={{ marginLeft:'auto' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or comment…"
            style={{
              padding:'7px 12px', minWidth:240,
              border:'1px solid #cbd5e1', borderRadius:8,
              fontSize:13, outline:'none',
            }}
          />
        </div>
      </div>

      {error && (
        <div style={{
          padding:'10px 14px', marginBottom:14,
          background:'#fff5f5', border:'1px solid #feb2b2',
          borderRadius:8, color:'#c53030', fontSize:13,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>Loading feedback…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign:'center', padding:50,
          border:'1px dashed #cbd5e1', borderRadius:12,
          color:'#64748b', background:'#f8fafc',
        }}>
          <div style={{ fontSize:32, marginBottom:8 }}>📭</div>
          <div style={{ fontSize:14, fontWeight:600, color:'#0f172a' }}>No feedback to show</div>
          <div style={{ fontSize:12.5, marginTop:4 }}>
            {popupItems.length === 0
              ? 'Submissions from the feedback popup will appear here.'
              : 'Try clearing your filter or search.'}
          </div>
        </div>
      ) : (
        <div style={{
          border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden',
          background:'#fff', boxShadow:'0 4px 14px rgba(13,79,82,0.06)',
        }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13.5 }}>
            <thead>
              <tr style={{ background:'#f1f5f9', fontSize:11.5, color:'#475569', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Question</th>
                <th style={th}>Comment</th>
                <th style={th}>When</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderTop:'1px solid #e2e8f0', background:'#fff' }}>
                      <td style={td}>
                        {(r.first_name || r.last_name)
                          ? <span>{[r.first_name, r.last_name].filter(Boolean).join(' ')}</span>
                          : <span style={{ color:'#94a3b8' }}>—</span>}
                      </td>
                      <td style={td}>
                        {r.email
                          ? <a href={`mailto:${r.email}`} style={{ color:'#0d7377' }}>{r.email}</a>
                          : <span style={{ color:'#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ ...td, maxWidth:240 }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#0f172a' }}>
                          {r.question || <span style={{ color:'#94a3b8' }}>(no question id)</span>}
                        </div>
                      </td>
                      <td style={{ ...td, maxWidth:280 }}>
                        <div style={{
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                          color: r.comment ? '#0f172a' : '#94a3b8',
                        }}>{r.comment || '—'}</div>
                      </td>
                      <td style={{ ...td, color:'#64748b', whiteSpace:'nowrap' }}>
                        {r.created_at ? new Date(r.created_at + 'Z').toLocaleString() : ''}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding:'11px 14px', textAlign:'left', fontWeight:700 };
const td = { padding:'12px 14px', verticalAlign:'top' };

function Kpi({ label, value, tint }) {
  return (
    <div style={{
      padding:'12px 14px', borderRadius:12,
      background:'#fff', border:'1px solid #e2e8f0',
      boxShadow:'0 4px 12px rgba(13,79,82,0.05)',
    }}>
      <div style={{ fontSize:11, color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color: tint || '#0f172a', marginTop:2 }}>{value}</div>
    </div>
  );
}

